import { createEntityAdapter } from "@reduxjs/toolkit";
import { RESULTS_PER_PAGE } from "constants";
import { apiSlice } from "features/api/apiSlice";
import { formatDate, formatFileSize } from "utils/helpers";

const audioFileAdapter = createEntityAdapter({});

const initialState = audioFileAdapter.getInitialState({ size: 0 });

export const audioFilesApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getAudioFiles: builder.query({
      query: (searchParams) =>
        `/audio_files?${searchParams}&limit=${RESULTS_PER_PAGE}`,
      transformResponse: (response) => {
        if (!response.results)
          return audioFileAdapter.setAll(initialState, []);

        const fetchedAudioFiles = response.results.map((audioFile) => {
          const {
            location,
            filesize,
            info,
            created_at,
            created_by,
            updated_at,
            ...audioFileProps
          } = audioFile;

          return {
            ...audioFileProps,
            filesize: formatFileSize(filesize),
            created_by: `${created_by.lastname} ${created_by.firstname}`,
            created_at: formatDate(created_at),
          }
        });

        return audioFileAdapter.setAll(
          { ...initialState, size: response?.total },
          fetchedAudioFiles
        );
      },
      providesTags: (result) =>
        result
          ? [
            ...result.ids.map((id) => ({ type: "AudioFile", id })),
            { type: "AudioFile", id: "PARTIAL-LIST" },
          ]
          : [{ type: "AudioFile", id: "LIST" }],
    }),
    getAudioFile: builder.query({
      query: (id) => `/audio_files/${id}`,
      providesTags: (result, error, id) => [{ type: "AudioFile", id }],
    }),
    downloadAudioFile: builder.mutation({
      query: (id) => ({
        url: `/audio_files/${id}/download`,
        method: "GET",
        responseHandler: (response) => response.blob(),
        cache: "no-cache",
      }),
    }),
    uploadAudioFile: builder.mutation({
      query: ({ file, category }) => {
        const formData = new FormData();
        formData.append("file", file); // Append the actual file
        return {
          url: `/audio_files/${category}`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: [{ type: "AudioFile", id: "PARTIAL-LIST" }],
    }),
    deleteAudioFile: builder.mutation({
      query: (id) => ({
        url: `/audio_files/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, id) => [
        { type: "AudioFile", id: result.id },
        { type: "AudioFile", id: "PARTIAL-LIST" },
      ],
    }),
  }),
})

export const {
  useGetAudioFilesQuery,
  useGetAudioFileQuery,
  useDownloadAudioFileMutation,
  useUploadAudioFileMutation,
  useDeleteAudioFileMutation,
} = audioFilesApiSlice